import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack} from 'views/components';
import {DeleteConfirmDialog} from 'views/module';

import AutomationsList from './automation_view/AutomationsList';
import AutomationsView from './automation_view/AutomationsView';
import AutomationsNew from './automation_new/AutomationsNew';
import {addUpdateRulesWithValueAny, removeUpdateRulesWithValueAny} from './util/automationHelper';

import getAutomationRules from './util/automationRules';

import backend from 'utils/backend';

const AutomationsScene = (props) => {
  const {automations: rawAutomations, automationsAdd, automationsRemove, statusProgressWrap: progressWrap} = props;

  const [activeNew, setActiveNew] = React.useState(false);
  const [editingAutomation, setEditingAutomation] = React.useState(null);
  const [isShowingConfirmDeleteDialog, setIsShowingConfirmDeleteDialog] = React.useState(false);
  const [selectedAutomationTaskRuleRn, setSelectedAutomationTaskRuleRn] = React.useState(null);
  const [assignees, setAssignees] = React.useState([]);
  const [assigneesLoading, setAssigneesLoading] = React.useState(false);
  const [automationInvocations, setAutomationInvocations] = React.useState([]);
  const [automationInvocationsLoading, setAutomationInvocationsLoading] = React.useState(false);

  const automations = React.useMemo(() => {
    const automationRules = getAutomationRules();

    return rawAutomations.map((rawAutomation) => {
      return addUpdateRulesWithValueAny(automationRules, rawAutomation);
    });
  }, [rawAutomations]);

  const {groupOptions, groupedAutomations} = React.useMemo(() => {
    const uniqueGroups = new Set();
    const groupedAutomationsMap = new Map();

    automations.forEach((automation) => {
      if (automation.groupName) {
        uniqueGroups.add(automation.groupName);
      }

      const groupName = automation.groupName || 'Ungrouped';
      const existingGroup = groupedAutomationsMap.get(groupName);

      if (existingGroup) {
        existingGroup.push(automation);
        return;
      }

      groupedAutomationsMap.set(groupName, [automation]);
    });

    return {
      groupOptions: Array.from(uniqueGroups),
      groupedAutomations: Array.from(groupedAutomationsMap, ([groupName, automations]) => ({
        groupName,
        automations,
      })),
    };
  }, [automations]);

  const selectedAutomation = React.useMemo(() => {
    return automations.find((automation) => automation.taskRuleRn === selectedAutomationTaskRuleRn);
  }, [selectedAutomationTaskRuleRn, automations]);

  React.useEffect(() => {
    if (!selectedAutomation) {
      setAssignees([]);
      setAssigneesLoading(false);
      return;
    }

    if (selectedAutomation.assigneeRns.length === 0) {
      setAssignees([]);
      setAssigneesLoading(false);
      return;
    }

    // if user switches automation, stale request payload can overwrite the new one
    let hasSelectedAutomationChanged = false;

    const loadAssignees = async () => {
      setAssignees([]);
      setAssigneesLoading(true);
      try {
        await progressWrap(async () => {
          const loadedAssignees = await Promise.all(selectedAutomation.assigneeRns.map(async (assigneeRn) => {
            const result = await backend.user.getUser(assigneeRn, false);
            return result.user;
          }));
          const recognizedAssignees = loadedAssignees.filter(Boolean);

          if (!hasSelectedAutomationChanged) {
            setAssignees(recognizedAssignees);
          }
        });
      } finally {
        if (!hasSelectedAutomationChanged) {
          setAssigneesLoading(false);
        }
      }
    };

    loadAssignees();

    return () => {
      hasSelectedAutomationChanged = true;
    };
  }, [selectedAutomation, progressWrap]);

  React.useEffect(() => {
    if (!selectedAutomation) {
      setAutomationInvocations([]);
      setAutomationInvocationsLoading(false);
      return;
    }

    let hasSelectedAutomationChanged = false;

    const loadAutomationInvocations = async () => {
      setAutomationInvocations([]);
      setAutomationInvocationsLoading(true);

      const taskFilters = [
        {key: 'taskRuleRn', value: selectedAutomation.taskRuleRn},
      ];
      const taskSortBy = {sortKey: 'dueDate', sortDirection: 'DESC'};

      try {
        await progressWrap(async () => {
          const response = await backend.task.queryCompanyTasks(taskFilters, taskSortBy, null);
          const tasks = response?.list;
          if (!hasSelectedAutomationChanged && tasks?.length) {
            setAutomationInvocations(tasks);
          }
        });
      } finally {
        if (!hasSelectedAutomationChanged) {
          setAutomationInvocationsLoading(false);
        }
      }
    };

    loadAutomationInvocations();

    return () => {
      hasSelectedAutomationChanged = true;
    };
  }, [selectedAutomation, progressWrap]);

  const openNew = React.useCallback(() => {
    setEditingAutomation(null);
    setActiveNew(true);
  }, []);

  const closeNew = React.useCallback(() => {
    setActiveNew(false);
    setEditingAutomation(null);
  }, []);

  const handleSelectAutomation = React.useCallback((automation) => {
    setSelectedAutomationTaskRuleRn(automation.taskRuleRn);
  }, []);

  const openDeleteConfirm = React.useCallback(() => {
    setIsShowingConfirmDeleteDialog(true);
  }, []);

  const handleEditAutomation = React.useCallback(() => {
    const automationToBeEdited = {
      ...selectedAutomation,
      initAssigneesInfo: assignees,
    };
    setEditingAutomation(automationToBeEdited);
    setActiveNew(true);
  }, [selectedAutomation, assignees]);

  const handleCreateOrUpdateAutomation = React.useCallback(async (automation, preExistingAutomationTaskRuleRn) => {
    const automationPayload = removeUpdateRulesWithValueAny(automation);
    await progressWrap(async () => {
      let result = null;
      if (preExistingAutomationTaskRuleRn) {
        result = await backend.automations.updateAutomation(preExistingAutomationTaskRuleRn, automationPayload);
        automationsRemove(preExistingAutomationTaskRuleRn);
      } else {
        result = await backend.automations.createAutomation(automationPayload);
      }

      automationsAdd(result.taskRuleDetail);
      setSelectedAutomationTaskRuleRn(result.taskRuleRn);

      closeNew();
    });
  }, [closeNew, progressWrap, automationsAdd]);

  const handleDeleteAutomation = React.useCallback(() => {
    if (!selectedAutomation) {
      return;
    }

    const taskRuleRn = selectedAutomation.taskRuleRn;
    setIsShowingConfirmDeleteDialog(false);

    progressWrap(async () => {
      await backend.automations.deleteAutomation(taskRuleRn);
      automationsRemove(taskRuleRn);
      setSelectedAutomationTaskRuleRn(null);
    });
  }, [progressWrap, selectedAutomation, automationsRemove]);

  return (
    <>
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        alignItems='stretch'
        sx={{
          width: '100%',
          height: {
            md: 'calc(99vh - 32px)',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            alignSelf: 'stretch',
            display: 'flex',
          }}
        >
          <AutomationsList
            groupedAutomations={groupedAutomations}
            openNew={openNew}
            onSelectAutomation={handleSelectAutomation}
            selectedAutomationTaskRuleRn={selectedAutomationTaskRuleRn}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            minHeight: 0,
            overflow: 'hidden',
            backgroundColor: '#FFFFFF',
          }}
        >
          <AutomationsView
            automation={selectedAutomation}
            onDelete={openDeleteConfirm}
            onEdit={handleEditAutomation}
            assigneesLoading={assigneesLoading}
            assignees={assignees}
            automationInvocations={automationInvocations}
            automationInvocationsLoading={automationInvocationsLoading}
          />
        </Box>
      </Stack>
      {
        activeNew &&
        <AutomationsNew
          onClose={closeNew}
          handleCreateAutomation={handleCreateOrUpdateAutomation}
          groupOptions={groupOptions}
          automation={editingAutomation}
        />
      }
      {
        isShowingConfirmDeleteDialog &&
        <DeleteConfirmDialog
          setDialogOpen={setIsShowingConfirmDeleteDialog}
          onClickConfirmDelete={handleDeleteAutomation}
        />
      }
    </>
  );
};

AutomationsScene.propTypes = {
  automations: PropTypes.array.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  automationsAdd: PropTypes.func.isRequired,
  automationsRemove: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  automations: state.automations.allAutomations,
});

export default connect(mapStateToProps, dispatch)(React.memo(AutomationsScene));
